import { BehaviorSubject } from 'rxjs';
import { Injector } from '@angular/core';
import { areArraysEqual } from '@fi-shared/utils/are-arrays-equal';
import {
    IChecklist2ReportDateRange,
    IChecklistV2ReportParameterFilter,
} from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { FiChecklist2ReportHelperService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-helper.service';
import { FiChecklist2ReportType } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-type';
import { FiChecklist2ReportOpenChecklistsService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-open-checklists.service';
import { FiChecklist2ReportHistoryService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-history.service';
import { cloneDeep } from '@fi-shared/utils';
import { Checkpoint2StatusType } from '@fi-shared/checklist/checklist-enum';

export class FiChecklistReportAvailableBusinessUnitsChecker {
    private _availableBusinessUnitIds$ = new BehaviorSubject<number[]>(undefined);
    get availableBusinessUnitIds$() {
        return this._availableBusinessUnitIds$.asObservable();
    }
    get availableBusinessUnitIds(): number[] {
        return this._availableBusinessUnitIds$.value;
    }

    private reportType: FiChecklist2ReportType;
    private auditMode: boolean;
    private fiChecklist2ReportHelperService: FiChecklist2ReportHelperService;
    private fiChecklist2ReportOpenChecklistsService: FiChecklist2ReportOpenChecklistsService;
    private fiChecklist2ReportHistoryService: FiChecklist2ReportHistoryService;

    constructor(injector: Injector, reportType: FiChecklist2ReportType, auditMode: boolean) {
        this.reportType = reportType;
        this.auditMode = auditMode;

        this.fiChecklist2ReportHelperService = injector.get(FiChecklist2ReportHelperService);
        this.fiChecklist2ReportOpenChecklistsService = injector.get(FiChecklist2ReportOpenChecklistsService);
        this.fiChecklist2ReportHistoryService = injector.get(FiChecklist2ReportHistoryService);
    }

    private selectedChecklistIds: number[];
    private selectedScheduleIds: number[];

    setAvailableBusinessUnitIds(
        availableBusinessUnitIds: number[],
        currentFilter: IChecklistV2ReportParameterFilter,
        dateRange?: IChecklist2ReportDateRange,
        forceLoad: boolean = false,
    ) {
        // reload on date range changed // todo
        const isFilterOnlyWithChecklistsSchedules =
            this.fiChecklist2ReportHelperService.isFilterOnlyWithChecklistsSchedules(currentFilter);

        /*if (!availableBusinessUnitIds) {
            availableBusinessUnitIds = this._availableBusinessUnitIds$.value;
        }*/

        if (!forceLoad && isFilterOnlyWithChecklistsSchedules && availableBusinessUnitIds) {
            // we can use currently fetched data for available business units
            this.selectedChecklistIds = currentFilter.ChecklistIds.slice();
            this.selectedScheduleIds = currentFilter.ScheduleIds.slice();
            this._availableBusinessUnitIds$.next(availableBusinessUnitIds);
        } else if (
            currentFilter.ChecklistIds &&
            currentFilter.ScheduleIds &&
            (forceLoad ||
                !this.selectedChecklistIds ||
                !this.selectedScheduleIds ||
                !areArraysEqual(this.selectedChecklistIds, currentFilter.ChecklistIds) ||
                !areArraysEqual(this.selectedScheduleIds, currentFilter.ScheduleIds))
        ) {
            // initial filter contains selected units/areas/business areas or selected checklist/schedule was changed
            const availableUnitsFilter: IChecklistV2ReportParameterFilter = {
                Guid: undefined,
                ChecklistIds: currentFilter.ChecklistIds,
                ScheduleIds: currentFilter.ScheduleIds,
            };
            if (this.reportType === FiChecklist2ReportType.AttentionRequired) {
                availableUnitsFilter.CheckpointStatusTypes = [Checkpoint2StatusType.NotOk];
                availableUnitsFilter.CheckpointIds = currentFilter.CheckpointIds;
            }

            this.selectedChecklistIds = currentFilter.ChecklistIds.slice();
            this.selectedScheduleIds = currentFilter.ScheduleIds.slice();

            this.loadAvailableBusinessUnitIds(availableUnitsFilter, dateRange).then((businessUnitIds) => {
                this._availableBusinessUnitIds$.next(businessUnitIds);
            });
        } else {
            // we can not fetch available units, checklists/schedules selection was not changed
        }
    }

    private loadAvailableBusinessUnitIds(
        availableUnitsFilter: IChecklistV2ReportParameterFilter,
        dateRange: IChecklist2ReportDateRange,
    ): Promise<number[]> {
        const promise = new Promise<number[]>((resolve, reject) => {
            if (
                this.reportType === FiChecklist2ReportType.OpenChecklists ||
                this.reportType === FiChecklist2ReportType.AttentionRequired
            ) {
                this.fiChecklist2ReportOpenChecklistsService
                    .getChecklistUnitIds(availableUnitsFilter, this.auditMode)
                    .then(resolve, reject);
            } else if (this.reportType === FiChecklist2ReportType.PastChecklists) {
                this.fiChecklist2ReportHistoryService
                    .getChecklistUnitIds(availableUnitsFilter, dateRange, this.auditMode)
                    .then(resolve, reject);
            } else {
                throw new Error(
                    `loadAvailableBusinessUnitIds: unknown report type ${FiChecklist2ReportType[this.reportType]}`,
                );
            }
        });
        promise.catch((err) => {
            console.log(err);
        });
        return promise;
    }
}
